import { css, html, LitElement } from 'lit-element';
import i18n from '@giswebgroup/ki-wcp-base/src/decorators/i18n';
import moment from 'moment';
import { orderBy } from 'lodash-es';
import nls from '../../locales/index.nls';

export default
@i18n(nls)
class KiCatchmentDetails extends LitElement {
  // language=CSS
  static styles = css`
    :host {
      display: flex;
      flex-direction:column;
    }

    .header {
      border-bottom: 1px solid lightgray;
      height: 56px;
    }

    ki-list{
      margin-top: 5px;
    }
    
    .header-label {
      padding: 17px 10px;
      display: inline-block;
      font-size: 1.3em;
    }

    .close {
      float: right;
      color: gray;
      font-size: 26px;
      line-height: 56px;
      padding-right: 20px;
    }

    .close:hover {
      cursor: pointer;
      color: lightgray;
    }

    .body {
      display: flex;
      flex-direction: column;
      overflow: auto;
    }

    .status-bar {
      background: rgba(0, 128, 0, 0.2);
      width: 100%;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: rgba(0, 128, 0, 1);
      font-size: 1.2em;
    }

    .status-bar ki-icon {
      width: 29px;
      text-align: center;
    }

    .status-bar span {
      padding: 0 8px;
    }

    .alarm-info {
      border-bottom: 1px solid lightgray;
    }

    .alarm-row {
      display: flex;
      flex-direction: row;
      padding: 10px;
    }

    .right {
      flex: 1;
      font-size: 14px;
      line-height: 1.5em;
      padding: 0 5px;
    }

    .alarm-icon {
      color: var(--theme-color, #0056a0);
      font-size: 1.2em;
      padding: 5px;
    }

    .alarm-label {
      color: var(--theme-color, #0056a0);
    }

    .station-item {
      display: flex;
      padding: 0;
      border-bottom: 1px solid lightgray;
    }

    .station {
      flex: 1;
      padding: 5px;
    }

    .station-catchment-level {
      flex: 0 0 40px;
      padding: 5px;
      white-space: nowrap;
      text-align: center;
      line-height: 80px;
    }

    .station-catchment-icon {
      padding: 0 4px;
      font-size: 25px;
    }

    .station-name {
      font-size: 16px;
      padding: 0 15px 5px 20px;
    }

    .label {
      color: gray;
    }

    .label,
    .value {
      font-size: 14px;
      padding: 0 20px;
    }
  `;

  static get properties() {
    return {
      catchment: { type: Object },
      tagStyles: { type: Object },

      label: { type: String },
      config: { type: Object }, // status
      stations: { type: Array },
      alarmTime: { type: String },
      alarmMessage: { type: String },
      alarmLevel: { type: String },
    };
  }

  constructor() {
    super();
    this.label = '';
    this.stations = [];
    this.formatter = new Intl.NumberFormat(this.i18n.language, { maximumFractionDigits: 2 });
    this.iconStyles = {
      0: html`<svg style="width:14px;fill:gray;" xmlns="http://www.w3.org/2000/svg" baseProfile="tiny" viewBox="0 0 16 16"><path d="M16.5 9l-5.74-5.5c-.79.76-.79 1.99 0 2.75l1.86 1.78H1.5v1.94h11.12l-1.86 1.78c-.79.76-.79 1.99 0 2.75L16.5 9z"/></svg>
          `,
      1: html`<svg style="width:14px;fill:gray;" xmlns="http://www.w3.org/2000/svg" baseProfile="tiny" viewBox="0 0 16 16"><path d="M9 1.5L3.5 7.24c.76.79 1.99.79 2.75 0l1.78-1.86V16.5h1.94V5.38l1.78 1.86c.76.79 1.99.79 2.75 0L9 1.5z"/></svg>
      `,
      '-1': html`<svg style="width:14px;fill:gray;" xmlns="http://www.w3.org/2000/svg" baseProfile="tiny" viewBox="0 0 16 16"><path d="M9 16.5l5.5-5.74c-.76-.79-1.99-.79-2.75 0l-1.78 1.86V1.5H8.03v11.12l-1.78-1.86c-.76-.79-1.99-.79-2.75 0L9 16.5z"/></svg>
      `,
    };
  
  }

  render() {
    if (!this.catchment) {
      return html``;
    }

    const alarmLevel = this.catchment.__tag;
    const level = (alarmLevel && this.catchmentTagStyles[alarmLevel]) || { backgroundColor: '', color: '', icon: '' };

    return html` <div class="header">
        <div class="header-label">${this.catchment.BASSIN_INFOCRUE}</div>
        <ki-icon class="close" icon="ki ki-times" @click="${this.close}"></ki-icon>
      </div>

      <div class="body">
        <div class="status-bar" style="background-color:${level.backgroundColor}; color:${level.color}">
          <ki-icon style="display:${level.icon ? 'inline-block': 'none'}" icon="${level.icon}"></ki-icon>
          <span>${level.label}</span>
        </div>

        <div class="alarm-info" style="display:${this.catchment.catchmentAlarmMessage ? 'block': 'none'}">
          <div class="alarm-row">
            <ki-icon class="alarm-icon" icon="ki ki-clock-o"></ki-icon>
            <div class="right">
              <span class="alarm-label">${this.i18n.t('message_time')}</span>
              <div>${this.catchment.catchmentAlarmTimestamp}</div>
            </div>
          </div>
          <div class="alarm-row">
            <ki-icon class="alarm-icon" icon="ki ki-envelope"></ki-icon>
            <div class="right">
              <span class="alarm-label">${this.i18n.t('message')}</span>
              <div style="white-space: pre-wrap;">${this.catchment.catchmentAlarmMessage || '-'}</div>
            </div>
          </div>
        </div>

        <ki-list class="list-items"> ${orderBy(this.catchment.stations, 'station_no').map(s => this.renderListItem(s))} </ki-list>
      </div>`;
  }

  renderListItem(station) {
    const marker = this.tagStyles[station.__tag] || { backgroundColor: '', color: '', icon: '' };
    return html`
      <ki-list-item class="station-item" @click="${this._clickHandler}" .station="${station}">
        <div class="station-catchment-level" style="background-color: ${marker.backgroundColor}; color:${marker.color};">
          <ki-icon icon="${marker.icon}"></ki-icon>
        </div>
        <div class="station">
          <table>
            <tr>
              <td colspan="2" class="station-name">${station.station_no} - ${station.station_name} ${station.river_name ? `- ${station.river_name}` : ""}</td>
            </tr>
            <tr>
              <td class="label">${this.i18n.t('parameter')}</td>
              <td class="value">${station.stationparameter_name}</td>
            </tr>
            <tr>
              <td class="label">${this.i18n.t('time')}</td>
              <td class="value">${(station.timestamp && moment(station.timestamp).format('L LT')) || '-'}</td>
            </tr>
            <tr>
              <td class="label">${this.i18n.t('value')}</td>
              <td class="value">${station.ts_value ===null ? ' - ': this.formatter.format(station.ts_value)} ${station.ts_value ? station.ts_unitsymbol : ''}</td>
            </tr>
            <tr>
            <td class="label">${this.i18n.t('tendency')}</td>
            <td class="value">${station.tendencyValue ===null ? ' - ': this.iconStyles[station.tendencyValue]}</td>
          </tr>
          </table>
        </div>
      </ki-list-item>
    `;
  }

  _clickHandler(event) {
    // event.currentTarget.classList.toggle('selected', !event.currentTarget.classList.contains('selected'));
    this.dispatchEvent(
      new CustomEvent('itemSelected', {
        detail: {
          stationId: event.currentTarget.station.station_id,
          stationPath: `${event.currentTarget.station.site_no}/${event.currentTarget.station.station_no}`,
          value: `${event.currentTarget.station.stationparameter_no}-hr`,
          element: event.currentTarget,
        },
      }),
    );
  }

  close() {
    this.dispatchEvent(
      new CustomEvent('close', {
        detail: {},
      }),
    );
  }
}

customElements.define('ki-catchment-details', KiCatchmentDetails);
